import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-full font-bold w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden px-[10px] py-[2px] text-[10px] border",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        // Subtle destructive (bg/border/text) matching the low/medium/high pattern
        destructiveSubtle:
          "bg-[rgba(229,62,62,0.1)] border-[rgba(229,62,62,0.2)] text-[#e53e3e]",
        // A destructive variant that matches our status-style (subtle) badges
        destructiveStatus:
          "bg-[rgba(244,63,94,0.1)] border-[rgba(244,63,94,0.2)] text-[#ef4444] font-medium",
        successSubtle:
          "bg-[rgba(56,161,105,0.1)] border-[rgba(56,161,105,0.2)] text-[#38a169]",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        editable:
          "bg-[var(--primary,#f6961d)]/30 border-[var(--primary,#f6961d)]/60 text-[var(--accent,#f6961d)] font-medium",
        low:
          "bg-[rgba(56,161,105,0.1)] border-[rgba(56,161,105,0.2)] text-[#38a169]",
        medium:
          "bg-[var(--primary-opacity-10)] border-[rgba(246,150,29,0.2)] text-[color:var(--primary)]",
        high:
          "bg-[rgba(229,62,62,0.1)] border-[rgba(229,62,62,0.2)] text-[#e53e3e]",
        extreme:
          "bg-[rgba(128,90,213,0.1)] border-[rgba(128,90,213,0.2)] text-[#805ad5]",
        // Status badge variants
        deleted:
          "bg-secondary dark:bg-card text-[#FF331F] font-medium border-1",
        success:
          "bg-secondary dark:bg-card text-[oklch(0.696_0.149_162.48)] font-medium border-1",
        warning:
          "bg-secondary dark:bg-card text-[oklch(0.769_0.165_70.08)] font-medium border-1",
        error:
          "bg-secondary dark:bg-card text-[oklch(0.637_0.208_25.331)] font-medium border-1",
        active:
          "bg-secondary dark:bg-card text-[oklch(0.696_0.149_162.48)] font-medium border-1",
        inactive:
          "bg-secondary dark:bg-card text-[oklch(0.769_0.165_70.08)] font-medium border-1",
        decommissioned:
          "bg-secondary dark:bg-card text-[oklch(0.637_0.208_25.331)] font-medium border-1",
        // Document type badge variants
        policy:
          "bg-[rgba(6,182,212,0.1)] border-[rgba(6,182,212,0.2)] text-[#0891b2] font-medium",
        procedure:
          "bg-[rgba(56,189,248,0.1)] border-[rgba(56,189,248,0.2)] text-[#0284c7] font-medium",
        form:
          "bg-[rgba(99,102,241,0.1)] border-[rgba(99,102,241,0.2)] text-[#4f46e5] font-medium",
        report:
          "bg-[rgba(244,63,94,0.1)] border-[rgba(244,63,94,0.2)] text-[#ef4444] font-medium",
        record:
          "bg-[rgba(34,197,94,0.1)] border-[rgba(34,197,94,0.2)] text-[#16a34a] font-medium",
        manual:
          "bg-[rgba(249,115,22,0.1)] border-[rgba(249,115,22,0.2)] text-[#ea580c] font-medium",
        plan:
          "bg-[rgba(245,158,11,0.1)] border-[rgba(245,158,11,0.2)] text-[#d97706] font-medium",
        guideline:
          "bg-[rgba(34,197,94,0.1)] border-[rgba(34,197,94,0.2)] text-[#16a34a] font-medium",
        standard:
          "bg-[rgba(99,102,241,0.1)] border-[rgba(99,102,241,0.2)] text-[#4f46e5] font-medium",
        checklist:
          "bg-[rgba(245,158,11,0.1)] border-[rgba(245,158,11,0.2)] text-[#d97706] font-medium",
        matrix:
          "bg-[rgba(99,102,241,0.1)] border-[rgba(99,102,241,0.2)] text-[#4f46e5] font-medium",
        presentation:
          "bg-[rgba(245,158,11,0.1)] border-[rgba(245,158,11,0.2)] text-[#d97706] font-medium",
        pdf:
          "bg-[rgba(99,102,241,0.1)] border-[rgba(99,102,241,0.2)] text-[#4f46e5] font-medium",
        evidence:
          "bg-[rgba(168,85,247,0.1)] border-[rgba(168,85,247,0.2)] text-[#9333ea] font-medium",
        // DED-943: Task Evidence - special system-managed document type
        taskevidence:
          "bg-[rgba(59,130,246,0.1)] border-[rgba(59,130,246,0.2)] text-[#3b82f6] font-medium",
        image:
          "bg-[rgba(168,85,247,0.1)] border-[rgba(168,85,247,0.2)] text-[#9333ea] font-medium",
        spreadsheet:
          "bg-[rgba(34,197,94,0.1)] border-[rgba(34,197,94,0.2)] text-[#16a34a] font-medium",
        database:
          "bg-[rgba(245,158,11,0.1)] border-[rgba(245,158,11,0.2)] text-[#d97706] font-medium",
        code:
          "bg-[rgba(99,102,241,0.1)] border-[rgba(99,102,241,0.2)] text-[#4f46e5] font-medium",
        website:
          "bg-[rgba(34,197,94,0.1)] border-[rgba(34,197,94,0.2)] text-[#16a34a] font-medium",
        template:
          "bg-[rgba(236,72,153,0.1)] border-[rgba(236,72,153,0.2)] text-[#db2777] font-medium",
        hardware:
          "bg-[rgba(139,92,246,0.1)] border-[rgba(139,92,246,0.2)] text-[#8b5cf6] font-medium",
        software:
          "bg-[rgba(168,85,247,0.1)] border-[rgba(168,85,247,0.2)] text-[#a855f7] font-medium",
        service:
          "bg-[rgba(192,132,252,0.1)] border-[rgba(192,132,252,0.2)] text-[#c084fc] font-medium",
        personnel:
          "bg-[rgba(216,180,254,0.1)] border-[rgba(216,180,254,0.2)] text-[#d8b4fe] font-medium",
        documentation:
          "bg-[rgba(232,121,249,0.1)] border-[rgba(232,121,249,0.2)] text-[#e879f9] font-medium",
        other:
          "bg-[rgba(107,114,128,0.1)] border-[rgba(107,114,128,0.2)] text-[#6b7280] font-medium",
        // ISO Standard variants
        'iso9001':
          "bg-[rgba(0, 112, 210, 0.1)] border-[rgba(0, 112, 210, 0.2)] text-[#0070d2] font-medium", // Dark Blue for Quality
        'iso14001':
          "bg-[rgba(75, 181, 67, 0.1)] border-[rgba(75, 181, 67, 0.2)] text-[#4bb543] font-medium", // Green for Environment
        'iso45001':
          "bg-[rgba(229, 62, 62, 0.1)] border-[rgba(229, 62, 62, 0.2)] text-[#e53e3e] font-medium", // Red for Safety
        'iso27001':
          "bg-[rgba(246, 150, 29, 0.1)] border-[rgba(246, 150, 29, 0.2)] text-[#f6961d] font-medium", // de.iterate Orange for Security
        'iso42001':
          "bg-[rgba(147, 51, 234, 0.1)] border-[rgba(147, 51, 234, 0.2)] text-[#9333ea] font-medium", // Purple for AI
        'iso27701':
          "bg-[rgba(251, 191, 36, 0.1)] border-[rgba(251, 191, 36, 0.2)] text-[#fbbf24] font-medium", // Yellow for Privacy

        // Policy Admin
        published: "bg-[rgba(56,161,105,0.1)] border-[rgba(56,161,105,0.2)] text-[#38a169]",
        approved: "bg-[rgba(56,161,105,0.1)] border-[rgba(56,161,105,0.2)] text-[#38a169]",
        inreview: "bg-[var(--primary-opacity-10)] border-[rgba(246,150,29,0.2)] text-[color:var(--primary)]",
        draft: "bg-[rgba(229,62,62,0.1)] border-[rgba(229,62,62,0.2)] text-[#e53e3e]",
        overdue: "bg-[rgba(239,68,68,0.1)] border-[rgba(239,68,68,0.2)] text-[#ef4444]",
        // Type column variants for policy admin
        process: "bg-[rgba(56,189,248,0.1)] border-[rgba(56,189,248,0.2)] text-[#0284c7] font-medium",
        privacy: "bg-[rgba(168,85,247,0.1)] border-[rgba(168,85,247,0.2)] text-[#9333ea] font-medium",
        notice: "bg-[rgba(245,158,11,0.1)] border-[rgba(245,158,11,0.2)] text-[#d97706] font-medium",

      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
